/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';
import {ResultCell} from "@/models/batch/result.cell";
import {ICellRenderer, ICellRendererParams} from "ag-grid-community";


/**
 *
 * @class BatchMonitorCellRenderer
 *
 * @brief cell rendering a result cell (monitoring mode)
 *
 * Unicode references:
 *
 *   https://en.wikipedia.org/wiki/X_mark
 *   https://en.wikipedia.org/wiki/Check_mark
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2022
 *
 */
export class BatchMonitorCellRenderer implements ICellRenderer {

  /** agGrid DOM element for display */
  public gui: HTMLElement;

  /** current value of the cell */
  public value: ResultCell = null;

  /** the status icons */
  public icons: Map<string, string> = new Map<string, string>([
    ['running',   '<i class="fa fa-cog" aria-hidden="true"></i>\n'],
    ['paused',    '<i class="fa fa-pause" aria-hidden="true"></i>\n'],
    ['created',   '<i class="fa fa-file" aria-hidden="true"></i>\n'],
    ['frozen',    '<i class="fa fa-camera" aria-hidden="true"></i>\n'],
    ['enabled',   '<i class="fa fa-toggle-on" aria-hidden="true"></i>\n'],
    ['disabled',  '<i class="fa fa-toggle-off" aria-hidden="true"></i>\n'],
    ['completed', '<i class="fa fa-check" aria-hidden="true"></i>\n'],
    ['error',     '<i class="fa fa-times" aria-hidden="true"></i>\n'],
    ['failed',    '<i class="fa fa-times" aria-hidden="true"></i>\n'],
    ['aborted',   '<i class="fa fa-stop" aria-hidden="true"></i>\n'],
    ['death',     '<i class="fa fa-exclamation-triangle" aria-hidden="true"></i>\n']
  ]);

  /** first time creation of the rendered cell. */
  public init(params: ICellRendererParams): void {

    const style = `color: rgba(245, 245, 245, 0.64);`;

    this.gui           = document.createElement('div');
    this.gui.innerHTML = `<span style="${style}"></span>`;

    /* render! */

    this.refresh(params);
  }

  /** return the currently rendered DOM */
  public getGui(): HTMLElement {

    return this.gui;
  }

  /** update the rendered DOM */
  public refresh(params: ICellRendererParams): boolean {

    const style = `color: rgba(245, 245, 245, 0.64);`;

    this.value = params?.value as ResultCell;

    if (!this.value || (this.value.resultId == 0)) {

      this.gui.innerHTML = `<span style="${style}">- - -</span>`;
      return ;
    }

    /* build up the cell graphics based on the steps, progress and status... */

    let html: string = '';

    html += `<div class="result-cell" >`;

    /* status icon */

    html += `<div class="result-cell-icon" style="background-color: ${this.cellBG()}">`;
    html += this.icons.get(this.value.status);
    html += `</div>`;

    /* slice */

    html += `<div class="result-cell-slice" style="background-color: ${this.stepBG('work_slice')}">`;
    html += `<span class="result-cell-label">S: ${this.sliceProgress()}</span>`;
    html += `</div>`;

    /* sim */

    html += `<div class="result-cell-sim" style="background-color: ${this.stepBG('work_sim')}">`;
    html += `<span class="result-cell-label">S: ${this.simProgress()}</span>`;
    html += `</div>`;

    /* KPI */

    html += `<div class="result-cell-kpi" style="background-color: ${this.stepBG('work_kpi')}">`;
    html += `<span class="result-cell-label">K: ${this.kpiProgress()}</span>`;
    html += `</div>`;

    html += `</div>`;

    /* render */

    this.gui.innerHTML = html;

    return true;
  }

  /** fetch the progress value of the slice step */
  public sliceProgress(): string {

    let progress: number = 0;


    if (typeof this.value.steps.work_slice != 'undefined') {

      if (this.value.steps.work_slice) {

        progress = Math.round(parseFloat(this.value.steps.work_slice));
      }
    }

    if (!this.value.resolved()) {

      return `${progress}%`;
    }

    /* if the cell errored out...then progress is 0 */

    if (this.value.sliceFailed()) {

      return `\u00D7`;
    }

    return `\u2713`;
  }

  /** fetch the progress value of the sim step */
  public simProgress(): string {

    let progress: number = 0;

    if (typeof this.value.steps.work_sim != 'undefined') {

      if (this.value.steps.work_sim) {

        progress = Math.round(parseFloat(this.value.steps.work_sim));
      }
    }

    if (!this.value.resolved()) {

      return `${progress}%`;
    }

    /* if the cell errored out...then progress is 0 */

    if (this.value.simFailed()) {

      return `\u00D7`;
    }

    return `\u2713`;
  }

  /** fetch the progress value of the kpi step */
  public kpiProgress(): string {

    let progress: number = 0;

    if (typeof this.value.steps.work_kpi != 'undefined') {

      if (this.value.steps.work_kpi) {

        progress = Math.round(parseFloat(this.value.steps.work_kpi));
      }
    }

    if (!this.value.resolved()) {

      return `${progress}%`;
    }

    /* if the cell errored out...then progress is 0 */

    if (this.value.kpiFailed()) {

      return `\u00D7`;
    }

    return `\u2713`;
  }


  /** step background for not yet resolved */
  public stepWorkingColor(): string {
    return '#9D8D1FFF';
  }

  /** step background for failed */
  public stepFailedColor(): string {
    return '#AF0000FF'
  }

  /** step background for completed */
  public stepCompletedColor(): string {
    return '#1b5e20';
  }

  public stepBG(step: string): string {

    if (!this.value.resolved() || (this.value.steps[step] < 100)) {

      /* it's not resolved yet */

      return this.stepWorkingColor();
    }

    let failed: boolean = false;

    switch(step) {

      case 'work_slice':
        failed = this.value.sliceFailed();
        break;

      case 'work_sim':
        failed = this.value.simFailed();
        break;

      case 'work_kpi':
        failed = this.value.kpiFailed();
        break;
    }

    if (failed) {

      /* step failed */

      return this.stepFailedColor();
    }

    /* this step is ok! */

    return this.stepCompletedColor();
  }

  /** fetch the color of the cell background */
  public cellBG(): string {

    if (!this.value.resolved()) {

      return '#9D8D1FFF';
    }

    switch (this.value.status) {
      case 'running':
      case 'paused':
      case 'created':
        return '#9D8D1FFF';

      case 'enabled':
        return '#515151';

      case 'frozen':
      case 'disabled':
        return 'transparent';

      case 'completed':
        return '#1b5e20';

      case 'error':
      case 'failed':
      case 'aborted':
      case 'death':
        return '#AF0000FF'

      default:
        return '';
    }

  }

  /** destructor for rendered DOM */
  public destroy() {

  }

}
